/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  AlertBanner,
  Avatar,
  Checkbox,
  Icon,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { testIds } from '../../../../tests/constants';
import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import { getCertKind, getEOCertKind } from '../../CertsDetailsList/Tile/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import CertsUserListDropdown from '../../CertsUserListDropdown';
import { LINE_ITEM_TYPES } from '../CertsActionButtonsGroup/constants';
import {
  ACCOUNT_DISPLAY_NAME,
  CHILD_ENTITLEMENT_DISPLAY_NAME,
  ENTITLEMENT_DISPLAY_NAME,
  EO_ACCOUNT_DISPLAY_NAME,
  EO_CHILD_ENTITLEMENT_DISPLAY_NAME,
  EO_ENTITLEMENT_DISPLAY_NAME,
  ROLE_DISPLAY_NAME,
  USER_DISPLAY_NAME,
} from '../CertsReassignModal/constants';
import { CERT_ENTITLEMENT_VALUEKEY } from '../constant';

function CertsConsultContent({
  consultModalDetails,
  nodeData,
  isIncludeAssociateChecked,
  setIsIncludedAssociateChecked,
  dropdownClassName,
  handleSelectUser,
  isSelectVisible,
  menuDropdownTestId,
  commentsDetails,
  isCritical,
  handleCommentDescription,
  commentInputTestId,
  selectedUser,
  isCommentRequired,
  isMobileWidth,
}) {
  const intl = useIntl();
  const {
    global: { certKey },
    core: { campaignType },
    lineActions: { errorMessage, isActionError, reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const { predefinedComments: { allowEditPredefinedComments = false } = {} } =
    useSelector((state) => state?.certifications?.details?.core || {});
  const [isVisible, setIsVisible] = useState(false);
  const isCertsKindUser =
    nodeData?.CERT_ENTITLEMENT_VALUEKEY === CERT_ENTITLEMENT_VALUEKEY.USER;
  const { CONSULT_MODAL_ALERT_BANNER_ERROR } = testIds;

  useEffect(() => {
    setIsVisible(isActionError);
  }, [isActionError]);

  /** to get lineitem display name */
  const getSelectedLineItemName = (lineItemType) => {
    let name = '';

    switch (lineItemType) {
      case LINE_ITEM_TYPES.USER:
        name = nodeData[USER_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ACCOUNT:
        name =
          nodeData[ACCOUNT_DISPLAY_NAME] || nodeData[EO_ACCOUNT_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ROLE:
        name = nodeData[ROLE_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ENTITLEMENT:
        name =
          nodeData[ENTITLEMENT_DISPLAY_NAME] ||
          nodeData[EO_ENTITLEMENT_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.CHILD_ENTITLEMENT:
        name =
          nodeData[CHILD_ENTITLEMENT_DISPLAY_NAME] ||
          nodeData[EO_CHILD_ENTITLEMENT_DISPLAY_NAME];
        break;
      default:
        break;
    }

    if (!name)
      name =
        messages['NOT_PROVIDED.ATTESTATIONS'] ||
        localize(intl, getMessageKey('NOT_PROVIDED.ATTESTATIONS'));

    return name;
  };

  return (
    <div>
      <div className='CertsConsultModal-containerHeader'>
        <AlertBanner
          className='consultCertifier-selectionWarn'
          colorTheme='Critical'
          title='Error'
          description={errorMessage}
          isVisible={isVisible}
          onCancel={() => setIsVisible(false)}
          dataTestId={CONSULT_MODAL_ALERT_BANNER_ERROR}
        />
        <div className='CertsConsultModal-details'>
          <Typography
            kind='h4'
            mobile='h4-d'
            className='CertsConsultModal-containerTitle'>
            {messages.CONSULT_ITEMS ||
              localize(intl, getMessageKey('CONSULT_ITEMS'))}
            {/* {`Selected ${consultModalDetails.lineItemKind}`} */}
          </Typography>
          {/* only for user-> accounts ->entitilements  when reassign for accounts */}
          {consultModalDetails?.isNoteRequired && (
            <div className='CertsReassignModal-containerNote'>
              <Tooltip
                openDelay={500}
                text={consultModalDetails?.noteText}
                maxWidth='20rem'
                placement={isMobileWidth ? 'top' : 'right'}
                offset={8}
                trigger={
                  <Icon
                    kind='AlertInfoFilledSeparateColors'
                    color='--color-foreground-info-medium'
                    className='CertsReassignModal-containerNoteIcon'
                  />
                }
              />
            </div>
          )}
          {/* only for user-> accounts ->entitilements  when reassign for accounts */}
        </div>
        <div className='CertsConsultModal-container-userDetails'>
          {isCertsKindUser ? (
            <Avatar size='small' />
          ) : (
            <Icon
              kind={
                isEOCampaign ? getEOCertKind(nodeData) : getCertKind(nodeData)
              }
              color='navy-900'
              size='smallMedium'
            />
          )}
          <Typography
            kind='h4'
            mobile='body2-d'
            className='CertsConsultModal-container-userDetailsName'>
            {getSelectedLineItemName(consultModalDetails?.lineItemKind)}
          </Typography>
        </div>
        {consultModalDetails?.isIncludeAssociatedEnt && (
          <div className='CertsConsultModal-includeCheckbox'>
            <Checkbox
              id='consult-content-checkbox'
              name='checkbox2'
              kind='singleLine'
              isChecked={isIncludeAssociateChecked}
              onChange={(event) =>
                setIsIncludedAssociateChecked(event.target.checked)
              }
              label={consultModalDetails?.isIncludeAsscLabel}
              labelPosition='before'
            />
          </div>
        )}
      </div>
      <div className='CertsConsultModal-containerCertifierSelection'>
        <div className='CertsConsultModal-DropdownAsterisk'>*</div>
        <Typography
          kind='h4'
          desktop='h4-d'
          mobile='h4-d'
          className='CertsConsultModal-container-certifier'>
          {messages.CONSULT_SELECT_CERTIFIER ||
            localize(intl, getMessageKey('CONSULT_SELECT_CERTIFIER'))}
        </Typography>
        <CertsUserListDropdown
          certKey={certKey}
          className={dropdownClassName}
          defaultReassignee={reassignConfigMap?.defaultReassignee}
          defaultAssigneName={reassignConfigMap?.defaultReassigneeName}
          certifierUserKey={reassignConfigMap?.certifierUserKey}
          onSelect={(user) => {
            handleSelectUser(user);
          }}
          isCritical={isSelectVisible}
          selectedUser={selectedUser}
          placeholder={
            messages.CONSULT_PLACEHOLDER ||
            localize(intl, getMessageKey('CONSULT_PLACEHOLDER'))
          }
          inputFieldTestId={menuDropdownTestId}
        />
      </div>
      {isCommentRequired && (
        <CertsModalCommentBox
          defaultComments={commentsDetails?.predefComments}
          showAlert={isCritical}
          onChange={handleCommentDescription}
          className='CertsConsultModal-comments'
          dataTestId={commentInputTestId}
          label={
            messages['comments.ATTESTATIONS'] ||
            localize(intl, getMessageKey('comments.ATTESTATIONS'))
          }
          allowEditPredefinedComments={allowEditPredefinedComments}
        />
      )}
    </div>
  );
}

CertsConsultContent.propTypes = {
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  consultModalDetails: PropTypes.shape({
    isIncludeAssociatedEnt: PropTypes.bool,
    isNoteRequired: PropTypes.bool,
    noteText: PropTypes.string,
    isIncludeAsscLabel: PropTypes.string,
    lineItemKind: PropTypes.string,
  }).isRequired,
  isIncludeAssociateChecked: PropTypes.bool.isRequired,
  setIsIncludedAssociateChecked: PropTypes.func.isRequired,
  dropdownClassName: PropTypes.string.isRequired,
  handleSelectUser: PropTypes.func.isRequired,
  isSelectVisible: PropTypes.bool.isRequired,
  menuDropdownTestId: PropTypes.bool.isRequired,
  commentsDetails: PropTypes.shape({
    commentDescription: PropTypes.string.isRequired,
    predefComments: PropTypes.arrayOf(PropTypes.string), // Optional array of strings
    isCommentApplied: PropTypes.bool.isRequired,
    showAlert: PropTypes.bool.isRequired,
  }).isRequired,
  isCritical: PropTypes.bool.isRequired,
  handleCommentDescription: PropTypes.func.isRequired,
  commentInputTestId: PropTypes.string.isRequired,
  selectedUser: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      username: PropTypes.string.isRequired,
      firstname: PropTypes.string.isRequired,
      lastname: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      displayname: PropTypes.string.isRequired,
      manager: PropTypes.number.isRequired,
    }),
    PropTypes.oneOf([null]),
  ]).isRequired,
  isCommentRequired: PropTypes.bool.isRequired,
  isMobileWidth: PropTypes.bool.isRequired,
};

export default CertsConsultContent;
